import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { TypeaheadSearch, TypeaheadOption } from './typeahead-search';

import { useApi } from '@/hooks/use-api';
import { dataService } from '@/services/data-service';
import type { User } from '@/types/models';

export type UserTypeaheadProps = {
	value: User | null;
	onSelect: (user: User | null) => void;
	placeholder?: string;
	excludeIds?: string[];
	label?: string;
	testID?: string;
};

export const UserTypeahead: React.FC<UserTypeaheadProps> = ({
	value,
	onSelect,
	placeholder = 'Search users...',
	label,
	testID,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | undefined>();
	const [users, setUsers] = useState<User[]>([]);
	const { user: currentUser } = useApi();

	const handleSelect = useCallback((option: TypeaheadOption | null) => {
		if (option) {
			const user = users.find(u => u.id === option.id);
			if (user) {
				onSelect(user);
			}
		} else {
			onSelect(null);
		}
	}, [users, onSelect]);

	const onSearch = useCallback((text: string) => {
		setLoading(true);
		setError(undefined);

		dataService.getUsers({
			search: text,
			limit: 50,
		})
			.then((response) => {
				const results = response.data;
				if (results.length === 0) {
					console.log('*** NO USERS FOUND ***', {
						text,
						results,
					});
				} else {
					// If there was no query, sort the results alphabetically by name,
					// but always show "me" first
					if (text.length === 0) {
						setUsers(results.sort((a, b) => {
							if (currentUser) {
								if (a.id === currentUser.id) {
									return -1;
								}
								if (b.id === currentUser.id) {
									return 1;
								}
							}
							return a.name.localeCompare(b.name);
						}));
					} else {
						setUsers(results);
					}
				}
				setLoading(false);
			})
			.catch((err) => {
				console.error('Error searching users:', err);
				setError('Failed to search users');
				setLoading(false);
			});
	}, [currentUser]);

	useEffect(() => {
		onSearch('');
	}, [onSearch]);

	const options: TypeaheadOption[] = useMemo(() => users.map(user => ({
		id: user.id,
		label: user.name,
		description: user.email,
	})), [users]);

	return (
		<TypeaheadSearch
			maxResults={50}
			selectedOption={value ? {
				id: value.id,
				label: value.name,
				description: value.email,
			} : null}
			onSearch={onSearch}
			onSelect={handleSelect}
			options={options}
			loading={loading}
			error={error}
			placeholder={placeholder}
			label={label}
			testID={testID}
		/>
	);
};
